import { Skeleton } from "antd"
import { useTranslation } from "react-i18next"

import { surfaceColor, surfaceText } from "../../../constants/surface-tokens"
import type { Notification } from "../types/notification.types.ts"
import { NotificationsList } from "./NotificationsList.tsx"

interface NotificationsContentProps {
  notifications: Notification[]
  hasNewNotification: boolean
  isModal?: boolean
  fullHeight?: boolean
  onMarkAsRead: (id: number | null) => void
  onCardClick: (notification: Notification) => void
  onArchive: (id: number | null) => void
  hasMore: boolean
  onLoadMore: () => void
  refetchNotifications: () => void
  fetching: boolean
  /** True when a search or filter is on, so "nothing here" can say which. */
  isFiltered?: boolean
  errored?: boolean
  onViewAll?: () => void
  density?: "compact" | "comfortable"
  showSecondaryActions?: boolean
}

/**
 * The bell's own list: ~440px wide on a desktop, capped against the viewport
 * so it can never run off a short screen, with the footer pinned below the
 * scroll area rather than at the end of it.
 */
const BELL_WIDTH = 440
/** Header, filter chips, footer and the shell's own top bar. */
const BELL_CHROME = 260

export function NotificationsContent({
  notifications,
  hasNewNotification,
  isModal = false,
  fullHeight = false,
  onMarkAsRead,
  onCardClick,
  onArchive,
  hasMore,
  onLoadMore,
  refetchNotifications,
  fetching,
  isFiltered = false,
  errored = false,
  onViewAll,
  density = "comfortable",
  showSecondaryActions = true
}: NotificationsContentProps) {
  const { t } = useTranslation()

  const listHeight = fullHeight
    ? "calc(100dvh - 91px)"
    : isModal
      ? "min(70vh, 640px)"
      : `min(60vh, calc(100dvh - ${BELL_CHROME}px))`

  return (
    <div
      data-testid="notifications-content"
      style={{
        width: isModal ? "100%" : `min(${BELL_WIDTH}px, calc(100vw - 32px))`,
        display: "flex",
        flexDirection: "column",
        ...(fullHeight && { height: "100%" })
      }}
    >
      {hasNewNotification && (
        <button
          type="button"
          onClick={refetchNotifications}
          data-testid="notifications-refresh"
          style={{
            alignSelf: "center",
            margin: "8px 0",
            padding: "5px 14px",
            borderRadius: 999,
            border: `1px solid ${surfaceColor.accent}`,
            background: surfaceColor.tealPale,
            color: surfaceColor.teal,
            cursor: "pointer",
            ...surfaceText.button
          }}
        >
          {t("notifications.bell.newNotification")}
        </button>
      )}

      <div style={{ flex: 1, minHeight: 0 }}>
        {fetching ? (
          <div style={{ padding: 16 }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        ) : errored ? (
          <EmptyState
            title={t(
              "notifications.bell.errorTitle",
              "Notifications could not load"
            )}
            body={t(
              "notifications.bell.errorBody",
              "The connection dropped. Check back in a moment."
            )}
            action={{
              label: t("notifications.bell.retry", "Try again"),
              onClick: refetchNotifications
            }}
            testId="notifications-error"
          />
        ) : notifications.length === 0 ? (
          isFiltered ? (
            <EmptyState
              title={t(
                "notifications.bell.noMatches",
                "Nothing matches these filters"
              )}
              body={t(
                "notifications.bell.noMatchesBody",
                "Try a different search term, or clear the filters."
              )}
              testId="notifications-no-matches"
            />
          ) : (
            <EmptyState
              title={t("notifications.bell.empty")}
              body={t(
                "notifications.bell.emptyBody",
                "You are up to date. New activity on your leads and projects will appear here."
              )}
              testId="notifications-empty"
            />
          )
        ) : (
          <NotificationsList
            notifications={notifications}
            onMarkAsRead={onMarkAsRead}
            onCardClick={onCardClick}
            onArchive={onArchive}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            height={listHeight}
            density={density}
            showSecondaryActions={showSecondaryActions}
          />
        )}
      </div>

      {/* Pinned below the scroll area, not at the end of it — a feed with
          hundreds of rows must never bury the way to the full list. */}
      {onViewAll && (
        <footer
          style={{
            borderTop: `1px solid ${surfaceColor.border}`,
            padding: "10px 16px",
            background: surfaceColor.surface
          }}
        >
          <button
            type="button"
            onClick={onViewAll}
            data-testid="notifications-view-all"
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              ...surfaceText.button,
              color: surfaceColor.teal
            }}
          >
            {t(
              "notifications.bell.viewAllNotifications",
              "View all notifications"
            )}{" "}
            →
          </button>
        </footer>
      )}
    </div>
  )
}

function EmptyState({
  title,
  body,
  action,
  testId
}: {
  title: string
  body: string
  action?: { label: string; onClick: () => void }
  testId: string
}) {
  return (
    <div
      data-testid={testId}
      style={{ padding: "40px 24px", textAlign: "center" }}
    >
      <p
        style={{
          ...surfaceText.actionTitle,
          color: surfaceColor.ink,
          margin: "0 0 6px"
        }}
      >
        {title}
      </p>
      <p
        style={{ ...surfaceText.meta, color: surfaceColor.inkMuted, margin: 0 }}
      >
        {body}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          style={{
            marginTop: 14,
            padding: "6px 14px",
            borderRadius: 6,
            border: `1px solid ${surfaceColor.accent}`,
            background: surfaceColor.surface,
            color: surfaceColor.teal,
            cursor: "pointer",
            ...surfaceText.button
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
